import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSwatchbook } from '@fortawesome/sharp-solid-svg-icons';
import { DropdownLabelRenderer } from 'components/dropdown/Dropdown';
import { DropdownWithStylableTrigger } from 'components/user_menu/components';
import { useFeatureFlag } from 'configcat-react';
import { useTheme } from 'contexts/ThemeContext';
import styled from 'styled-components';
import { ThemeType } from 'styles/colorMode';
import { useColorMode } from 'styles/theme';
import { menuButtonStyles } from 'styles/theme_styles/button';
import { usePendoTrackTheme } from 'styles/usePendoTrackTheme';
import { tokens } from 'styles/tokens';

type ThemePickerOption = { Value: ThemeType; Text: string };
const ThemePickerOptionValues = [
    ThemeType.light,
    ThemeType.darkOriginal,
    ThemeType.dark,
    ThemeType.night,
];

function themeDisplayName(value: ThemeType | undefined) {
    switch (value) {
        case ThemeType.light:
            return 'Light';
        case ThemeType.darkOriginal:
            return 'Dark';
        case ThemeType.dark:
            return 'Navy';
        case ThemeType.night:
            return 'Night';
        default:
            return '';
    }
}

export const ThemePickerOptions: ThemePickerOption[] = ThemePickerOptionValues.map(
    (v) => ({ Value: v, Text: themeDisplayName(v) }),
);

// our dropdown is not very composable without writing large swaths of
// reimplementing base dropdown behavior, so... behold Arkane Invokations!
const StyledDropdown = styled(DropdownWithStylableTrigger)<{ $active?: boolean }>`
    ${menuButtonStyles}

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: stretch;
    height: 100%;
    flex-grow: 1;

    border-bottom: 0;

    padding: 0;
    margin: 0;

    & > *,
    & > * > * {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: stretch;

        box-sizing: content-box;
        flex-grow: 1;
        padding: 0;
        margin: 0;
    }
` as typeof DropdownWithStylableTrigger;

const Trigger = styled.span<{ $active?: boolean }>`
    ${menuButtonStyles}

    flex-grow: 1;
`;

const themeColors = {
    light: '#dfe7f0',
    dark: '#2f5cc2',
    night: '#101218',
    darkOriginal: '#23262E',
};

const ThemeColorCircle = styled.span<{ value: ThemeType | undefined }>`
    width: 12px;
    height: 12px;
    min-width: 12px;
    min-height: 12px;
    border-radius: 50%;
    border: 1px solid ${tokens.border.default};
    background-color: ${(props) => {
        switch (props.value) {
            case ThemeType.light:
                return themeColors.light;
            case ThemeType.dark:
                return themeColors.dark;
            case ThemeType.night:
                return themeColors.night;
            case ThemeType.darkOriginal:
                return themeColors.darkOriginal;
            default:
                return 'transparent';
        }
    }};
`;

function TopBarMenuIcon({ isOpen }: { isOpen: boolean }) {
    if (!useFeatureFlag('hasThemes', false).value) {
        return null;
    }
    return (
        <Trigger $active={isOpen}>
            <FontAwesomeIcon icon={faSwatchbook} />
        </Trigger>
    );
}

function ThemePicker({ renderLabel }: { renderLabel: DropdownLabelRenderer<ThemeType> }) {
    const colorMode = useColorMode();
    const themeToggle = useTheme();
    const trackTheme = usePendoTrackTheme();

    if (!useFeatureFlag('hasThemes', false).value) {
        return null;
    }

    return (
        <StyledDropdown<ThemeType>
            id="theme-dropdown"
            renderLabel={renderLabel}
            data={ThemePickerOptionValues}
            value={colorMode}
            onChange={(value) => {
                trackTheme(value, colorMode, 'top_bar');
                themeToggle.setColorMode(value);
            }}
            dataKey={(item) => item}
            renderItem={(info) => <ThemePickerDropdownItem item={info.item} />}
            trigger={{ hideChevron: true }}
            popup={{ width: '110px' }}
        />
    );
}

export function ThemePickerDropdownItem({ item }: { item: ThemeType }) {
    return (
        <span className="flex gap-5 align-center">
            <ThemeColorCircle value={item} /> {themeDisplayName(item)}
        </span>
    );
}

export function TopBarThemePicker() {
    return (
        <ThemePicker renderLabel={(info) => <TopBarMenuIcon isOpen={info.isOpen} />} />
    );
}
